import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  serverTimestamp 
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import toast from 'react-hot-toast';

// ESDRAS OFFICIAL COLORS
const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function SupportTicket({ onClose }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = auth.currentUser;

  // Style constants integrated to avoid "Module Not Found" errors
  const modalOverlay = { 
    position: 'fixed', 
    top: 0, 
    left: 0, 
    width: '100%', 
    height: '100%', 
    background: 'rgba(0,0,0,0.85)', 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    zIndex: 3000, 
    backdropFilter: 'blur(8px)' 
  };

  const modalContent = { 
    background: '#010810', 
    width: '90%', 
    maxWidth: '500px', 
    borderRadius: '24px', 
    border: `1px solid ${GOLD}`, 
    padding: '2rem', 
    color: 'white', 
    maxHeight: '90vh', 
    overflowY: 'auto' 
  };

  const inputStyle = { 
    width: '100%', 
    background: 'rgba(255,255,255,0.05)', 
    border: '1px solid rgba(184,134,11,0.3)', 
    padding: '12px', 
    borderRadius: '12px', 
    color: 'white', 
    marginBottom: '15px', 
    outline: 'none',
    boxSizing: 'border-box'
  };

  const submitBtn = { 
    width: '100%', 
    background: GOLD, 
    color: NAVY, 
    border: 'none', 
    padding: '14px', 
    borderRadius: '12px', 
    fontWeight: 'bold', 
    cursor: 'pointer',
    transition: 'opacity 0.2s'
  };

  const ticketCard = { 
    background: 'rgba(255,255,255,0.03)', 
    padding: '15px', 
    borderRadius: '12px', 
    marginBottom: '10px', 
    borderLeft: `4px solid ${GOLD}` 
  };

  useEffect(() => {
    if (!user) return;
    // Synced with your Cloud Functions triggers
    const q = query(
      collection(db, 'support_tickets'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      setTickets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !message) return toast.error("Please fill all fields");
    
    setLoading(true);
    try {
      // This triggers the Cloud Function 'onSupportTicketCreated'
      await addDoc(collection(db, 'support_tickets'), {
        userId: user.uid,
        userName: user.displayName || 'Member',
        userEmail: user.email,
        subject,
        message,
        status: 'open',
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
      toast.success("Ticket submitted to Concierge!");
      setSubject('');
      setMessage('');
    } catch (err) {
      console.error("Ticket Error:", err);
      toast.error("Failed to send ticket");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={modalOverlay}>
      <div style={modalContent}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h2 style={{ color: GOLD, margin: 0, fontSize: '1.2rem', letterSpacing: '1px' }}>HELP & SUPPORT</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <label style={{ fontSize: '0.7rem', color: GOLD, fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>SUBJECT</label>
          <input 
            style={inputStyle} 
            placeholder="e.g., Payment Issue" 
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <label style={{ fontSize: '0.7rem', color: GOLD, fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>MESSAGE</label>
          <textarea 
            style={{ ...inputStyle, minHeight: '100px', resize: 'none' }} 
            placeholder="Describe your issue in detail..." 
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />

          <button type="submit" disabled={loading} style={{...submitBtn, opacity: loading ? 0.7 : 1}}>
            {loading ? "SENDING..." : "SUBMIT TICKET"}
          </button>
        </form>

        <div style={{ marginTop: '30px' }}>
          <h3 style={{ fontSize: '0.9rem', color: GOLD, borderBottom: `1px solid ${GOLD}33`, paddingBottom: '10px' }}>MY RECENT TICKETS</h3>
          {tickets.length === 0 ? (
            <p style={{ opacity: 0.5, fontSize: '0.8rem', textAlign: 'center', marginTop: '20px' }}>No tickets found.</p>
          ) : (
            tickets.map(t => (
              <div key={t.id} style={ticketCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ margin: 0, fontWeight: 'bold', fontSize: '0.85rem' }}>{t.subject}</p>
                  <span style={{ 
                    fontSize: '0.6rem', 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    background: t.status === 'open' ? GOLD : '#2ecc71',
                    color: t.status === 'open' ? NAVY : 'white',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}>
                    {t.status}
                  </span>
                </div>
                <p style={{ margin: '5px 0 0', fontSize: '0.75rem', opacity: 0.7 }}>{t.message.substring(0, 60)}...</p>
                
                {t.adminReply && (
                  <div style={{ marginTop: '10px', padding: '10px', background: 'rgba(184,134,11,0.1)', borderRadius: '8px', border: `1px solid ${GOLD}33` }}>
                    <p style={{ margin: 0, fontSize: '0.7rem', color: GOLD, fontWeight: 'bold' }}>ADMIN REPLY:</p>
                    <p style={{ margin: 0, fontSize: '0.75rem' }}>{t.adminReply}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
                   }
        
